'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import { useStore } from '@tanstack/react-form-nextjs'
import { LucideTarget, X } from 'lucide-react'
import { useTranslations } from 'next-intl'

import { FieldWrapper } from '@/components/form/field-wrapper'
import { useFieldContext } from '@/components/form/form'
import { TargetIcon2 } from '@/components/icons'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip'

import { isFieldInvalid } from '@/utils/form'

import { geocodingService } from '@/lib/geocoding/geocoding-service'
import { cn } from '@/utils'

import { MapModal } from './map-modal'

import { MapPosition } from '@/lib/map/types'

interface MapSelectorProps {
  className?: string
  clearable?: boolean
  customCoordinateFieldNames?: {
    latitude: string
    longitude: string
  }
  disabled?: boolean
  hideLabel?: boolean
  hideTextfeild?: boolean
  inputsWrapperClassName?: string
  isVertical?: boolean
  label?: string
  labelClass?: string
  placeholder?: string
  placeholderMap?: string
  required?: boolean
}

export function MapSelector({
  className = '',
  clearable = true,
  customCoordinateFieldNames = {
    latitude: 'address_latitude',
    longitude: 'address_longitude'
  },
  disabled = false,
  hideLabel = false,
  hideTextfeild = false,
  inputsWrapperClassName = '',
  isVertical = false,
  label,
  labelClass = '',
  placeholder,
  placeholderMap,
  required = false
}: MapSelectorProps) {
  const t = useTranslations('components.form.mapSelector')
  const defaultLabel = label ?? t('label')
  const defaultPlaceholder = placeholder ?? t('placeholder')
  const defaultPlaceholderMap = placeholderMap ?? t('placeholderMap')
  const [isMapOpen, setIsMapOpen] = useState(false)
  const [selectedShortAddress, setSelectedShortAddress] = useState<string>('')
  const [isLoadingAddress, setIsLoadingAddress] = useState(false)

  // Use field context for form integration
  const field = useFieldContext<string>()
  const addressFieldValue = field.state.value

  const addressFieldName = field.name
  const latFieldName = customCoordinateFieldNames.latitude
  const lngFieldName = customCoordinateFieldNames.longitude

  // Helper function to get nested value from form state
  const getNestedValue = (obj: unknown, path: string): unknown => {
    if (!path || !obj || typeof obj !== 'object') return undefined

    // Handle array notation: stops[0].latitude
    const parts = path.split(/[\.\[\]]/).filter(Boolean)
    let current: unknown = obj

    for (const part of parts) {
      if (current == null) return undefined
      if (typeof current !== 'object') return undefined
      current = (current as Record<string, unknown>)[part]
    }

    return current
  }

  const currentPosition: {
    hasCoorinates: boolean
    lat: null | number
    lng: null | number
  } = useStore(field.form.store, (state) => {
    const lat = latFieldName
      ? (getNestedValue(state.values, latFieldName) as number)
      : null
    const lng = lngFieldName
      ? (getNestedValue(state.values, lngFieldName) as number)
      : null

    return {
      hasCoorinates: !!lat && !!lng,
      lat,
      lng
    }
  })

  const coordinateErrorMessage =
    !currentPosition.hasCoorinates && t('latLngRequired')

  const showCoordinateErrors =
    required &&
    !currentPosition.hasCoorinates &&
    Boolean(coordinateErrorMessage) &&
    field.state.meta.isTouched
  const isInvalid = isFieldInvalid(field)
  const showAnyInvalid = isInvalid || showCoordinateErrors

  const prevPositionRef = useRef<{
    lat: null | number
    lng: null | number
  } | null>(null)

  // Fetch address for existing coordinates
  useEffect(() => {
    const fetchAddressForExistingValue = async () => {
      const position = currentPosition
      const hasChanged =
        prevPositionRef.current?.lat !== position.lat ||
        prevPositionRef.current?.lng !== position.lng

      if (
        position &&
        !selectedShortAddress &&
        position.lat &&
        position.lng &&
        hasChanged
      ) {
        prevPositionRef.current = { lat: position.lat, lng: position.lng }
        setIsLoadingAddress(true)
        try {
          const geocodingResult = await geocodingService.reverseGeocode({
            lat: position.lat,
            lng: position.lng
          })
          if (geocodingResult) {
            const longAddress =
              geocodingService.getShortAddress(geocodingResult)
            const shortAddress =
              geocodingService.getShortAddress(geocodingResult)

            setSelectedShortAddress(shortAddress)
            if (longAddress && addressFieldName) {
              field.form.setFieldValue(addressFieldName, longAddress)
            }
          }
        } catch (error) {
          console.log('Error fetching address for existing coordinates:', error)
        } finally {
          setIsLoadingAddress(false)
        }
      } else if (!position) {
        // Reset address when value is cleared
        setSelectedShortAddress('')
        prevPositionRef.current = null
      }
    }

    fetchAddressForExistingValue()
  }, [
    currentPosition.lat,
    currentPosition.lng,
    addressFieldName,
    field.form,
    selectedShortAddress
  ])

  const handleMapConfirm = useCallback(
    (position: MapPosition, address?: string) => {
      // Set latitude and longitude fields directly
      if (latFieldName) {
        field.form.setFieldValue(latFieldName, position.lat)
      }
      if (lngFieldName) {
        field.form.setFieldValue(lngFieldName, position.lng)
      }

      if (address && addressFieldName) {
        field.form.setFieldValue(addressFieldName, address)
      }

      setSelectedShortAddress(address || '')
    },
    [addressFieldName, field.form, latFieldName, lngFieldName]
  )

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    setSelectedShortAddress('')

    // Clear related latitude and longitude fields
    if (latFieldName) {
      field.form.setFieldValue(latFieldName, undefined)
    }
    if (lngFieldName) {
      field.form.setFieldValue(lngFieldName, undefined)
    }

    // Clear address field if it exists
    if (addressFieldName) {
      field.form.setFieldValue(addressFieldName, '')
    }
  }

  const handleOpenMap = () => {
    if (!disabled) {
      setIsMapOpen(true)
    }
  }

  return (
    <FieldWrapper
      classNames={{
        childrenWrapper: 'max-w-full',
        label: labelClass
      }}
      field={field}
      label={hideLabel ? undefined : defaultLabel}
      required={required}
    >
      <div
        className={cn(
          'flex flex-col gap-3',
          isVertical ? 'flex-col' : 'flex-col md:flex-row',
          inputsWrapperClassName
        )}
      >
        <div className={cn('w-full flex-1', hideTextfeild ? 'hidden' : '')}>
          <input
            className={cn(
              'border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-[42px] w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
              showAnyInvalid ? 'border-destructive' : ''
            )}
            data-invalid={isInvalid || undefined}
            disabled={disabled}
            onChange={(e) =>
              field.form.setFieldValue(addressFieldName, e.target.value)
            }
            placeholder={defaultPlaceholder}
            type="text"
            value={addressFieldValue}
          />
        </div>
        <div
          className={cn(
            'border-border hover:border-foreground bg-background flex h-[42px] max-w-1/4 items-center justify-between gap-2 overflow-hidden rounded-lg border p-1 px-3 transition-colors',
            disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
            showAnyInvalid ? 'border-destructive focus:border-destructive' : '',
            hideTextfeild ? 'max-w-full' : 'max-w-1/4',
            className
          )}
          data-invalid={isInvalid || undefined}
          onClick={handleOpenMap}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
              e.preventDefault()
              handleOpenMap()
            }
          }}
          role="button"
          tabIndex={disabled ? -1 : 0}
        >
          {currentPosition.hasCoorinates ? (
            <>
              <div className="flex items-center gap-1 rtl:flex-row-reverse">
                <TargetIcon2 className="text-destructive h-4 w-4" />
                <span className="text-foreground truncate text-sm">
                  {isLoadingAddress
                    ? t('loading')
                    : selectedShortAddress || t('selected')}
                </span>
              </div>
              {clearable && !disabled && (
                <button
                  aria-label={t('clearLocation')}
                  className="hover:bg-accent ms-1 rounded-full p-1 transition-colors"
                  onClick={handleClear}
                  type="button"
                >
                  <X className="text-muted-foreground h-3 w-3" />
                </button>
              )}
            </>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex w-full max-w-[100%] items-center justify-between gap-2 overflow-auto">
                  <span className="text-muted-foreground block max-w-[calc(100%-20px)] min-w-0 flex-1 truncate text-sm">
                    {placeholderMap || t('label')}
                  </span>
                  <LucideTarget className="text-destructive h-3 min-h-3 w-3 min-w-3 flex-shrink-0" />
                </div>
              </TooltipTrigger>
              <TooltipContent
                align="start"
                className="max-w-xs break-words whitespace-normal"
                side="bottom"
                sideOffset={8}
              >
                <p className="text-sm whitespace-normal">
                  {defaultPlaceholderMap}
                </p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      {!isInvalid && showCoordinateErrors && (
        <em className={cn('text-destructive text-xs')}>
          {coordinateErrorMessage}
        </em>
      )}

      <MapModal
        initialPosition={
          (currentPosition.lat &&
            currentPosition.lng && {
              lat: currentPosition.lat,
              lng: currentPosition.lng
            }) ||
          undefined
        }
        isOpen={isMapOpen}
        onClose={() => setIsMapOpen(false)}
        onConfirm={handleMapConfirm}
        title={defaultLabel}
      />
    </FieldWrapper>
  )
}
